import React, { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";

const LEAVE_TYPES = ["Casual", "Medical", "Annual"];

const emptyDefaults = () => ({
  Casual: 10,
  Medical: 10,
  Annual: 14,
});

export default function LeaveQuotaBulkAssign({ onAssigned }) {
  const { state } = useContext(NoteState);
  const { branch_id } = state;

  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [defaults, setDefaults] = useState(emptyDefaults());
  const [includeCarry, setIncludeCarry] = useState(false);
  const [filterBranch, setFilterBranch] = useState(branch_id || "");
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [periodMeta, setPeriodMeta] = useState(null);
  const [prevWindow, setPrevWindow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCreatePeriod, setShowCreatePeriod] = useState(false);
  const [newPeriod, setNewPeriod] = useState({
    name: "Leave Quota FY 2026-27",
    start_date: "2026-07-01",
    end_date: "2027-06-30",
    is_active: true,
  });
  const [attentionOnly, setAttentionOnly] = useState(false);

  const loadPeriods = async () => {
    try {
      const res = await UserServices.getLeaveQuotaPeriods();
      const list = res?.data?.data || [];
      setPeriods(list);
      if (res?.data?.defaults) {
        setDefaults((prev) => ({ ...prev, ...res.data.defaults }));
      }
      const active = res?.data?.active_period || list.find((p) => Number(p.is_active) === 1);
      if (active?.id) {
        setPeriodId(String(active.id));
        setShowCreatePeriod(false);
      } else if (list[0]?.id) {
        setPeriodId(String(list[0].id));
        setShowCreatePeriod(false);
      } else {
        setPeriodId("");
        setShowCreatePeriod(true);
      }
    } catch (err) {
      console.log(err);
      setShowCreatePeriod(true);
      toast.error("Unable to load leave periods. Create one below.");
    }
  };

  const loadBranches = async () => {
    try {
      const responce = await UserServices.branchName();
      const res = responce.data.data || [];
      setBranches([{ id: "", name: "All Branches" }, ...res.map((b) => ({ id: b.id, name: b.name }))]);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    loadPeriods();
    loadBranches();
  }, []);

  useEffect(() => {
    if (branch_id) setFilterBranch(branch_id);
  }, [branch_id]);

  const createPeriod = async () => {
    if (!newPeriod.name || !newPeriod.start_date || !newPeriod.end_date) {
      toast.error("Fill period name and dates.");
      return;
    }
    setLoading(true);
    try {
      const res = await UserServices.createLeaveQuotaPeriod(newPeriod);
      toast.success("Leave period created.");
      setShowCreatePeriod(false);
      await loadPeriods();
      if (res?.data?.data?.id) {
        setPeriodId(String(res.data.data.id));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message?.name?.[0] || "Unable to create period.");
    } finally {
      setLoading(false);
    }
  };

  const buildEditableRows = (data) =>
    (data || []).map((row) => {
      const types = {};
      LEAVE_TYPES.forEach((type) => {
        const t = row.types?.[type] || {};
        types[type] = {
          selected: true,
          taken_this_period: Number(t.taken_this_period || 0),
          previous_balance: Number(t.previous_balance || 0),
          number_of_leaves: Number(
            t.existing_entitlement ?? t.suggested_quota ?? defaults[type] ?? 0
          ),
          carried_forward: Number(t.suggested_carry || 0),
        };
      });
      return {
        user_id: row.user_id,
        name: row.name,
        branch_id: row.branch_id,
        already_assigned: !!row.already_assigned,
        needs_attention: !!row.needs_attention,
        types,
      };
    });

  const loadPreview = async () => {
    if (!periodId) {
      toast.error("Create or select a leave period first.");
      return;
    }
    setLoading(true);
    try {
      const res = await UserServices.previewLeaveQuotaBulk({
        period_id: Number(periodId),
        branch_id: filterBranch || null,
        include_carry_forward: includeCarry,
        defaults,
      });
      setPeriodMeta(res.data.period || null);
      setPrevWindow(res.data.previous_period_window || null);
      setSummary(res.data.summary || null);
      setRows(buildEditableRows(res.data.data));
      if (res.data.defaults) {
        setDefaults((prev) => ({ ...prev, ...res.data.defaults }));
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message?.period_id?.[0] ||
          err?.response?.data?.message ||
          "Unable to load bulk preview."
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const updateTypeField = (userId, type, field, value) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.user_id !== userId) return row;
        const nextTypes = { ...row.types };
        const current = { ...nextTypes[type] };
        const numeric = field === "selected" ? value : Number(value);
        current[field] = field === "selected" ? !!value : Number.isFinite(numeric) ? numeric : 0;

        if (field === "carried_forward") {
          const base = Number(defaults[type] || 0);
          current.number_of_leaves = base + Number(current.carried_forward || 0);
          if (current.taken_this_period > current.number_of_leaves) {
            current.number_of_leaves = current.taken_this_period;
          }
        }
        nextTypes[type] = current;
        return { ...row, types: nextTypes };
      })
    );
  };

  const applyDefaultsToAll = () => {
    setRows((prev) =>
      prev.map((row) => {
        const types = { ...row.types };
        LEAVE_TYPES.forEach((type) => {
          const t = { ...types[type] };
          const carry = includeCarry ? Number(t.carried_forward || t.previous_balance || 0) : 0;
          t.carried_forward = includeCarry ? carry : 0;
          t.number_of_leaves = Math.max(
            Number(defaults[type] || 0) + t.carried_forward,
            Number(t.taken_this_period || 0)
          );
          types[type] = t;
        });
        return { ...row, types };
      })
    );
    toast.info("Defaults applied to the grid. Review highlighted rows before assigning.");
  };

  const assignAll = async () => {
    if (!periodId || !rows.length) {
      toast.error("Load preview first.");
      return;
    }

    const underTaken = rows.some((row) =>
      LEAVE_TYPES.some((type) => {
        const t = row.types[type];
        return t.selected && Number(t.number_of_leaves) < Number(t.taken_this_period);
      })
    );
    if (underTaken) {
      toast.error("Some quotas are below leaves already taken this period. Adjust those rows.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        period_id: Number(periodId),
        staff: rows.map((row) => ({
          user_id: row.user_id,
          branch_id: row.branch_id,
          types: LEAVE_TYPES.reduce((acc, type) => {
            const t = row.types[type];
            acc[type] = {
              selected: !!t.selected,
              number_of_leaves: Number(t.number_of_leaves || 0),
              carried_forward: Number(t.carried_forward || 0),
            };
            return acc;
          }, {}),
        })),
      };
      const res = await UserServices.assignLeaveQuotaBulk(payload);
      toast.success(
        `Assigned. Created ${res.data.created || 0}, updated ${res.data.updated || 0}.`
      );
      if (typeof onAssigned === "function") onAssigned();
      await loadPreview();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Bulk assign failed.");
    } finally {
      setSaving(false);
    }
  };

  const visibleRows = useMemo(() => {
    if (!attentionOnly) return rows;
    return rows.filter((r) => r.needs_attention);
  }, [rows, attentionOnly]);

  return (
    <div className="border rounded-3 p-3 mb-4 bg-light">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h5 className="mb-1">Bulk Leave Quota (Fiscal Year)</h5>
          <p className="mb-0 text-muted small">
            Defaults match current DB practice: Casual <strong>10</strong>, Medical{" "}
            <strong>10</strong>, Annual <strong>14</strong> (editable). Staff who already
            took leave this year are listed first so HR can adjust before assign.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-dark"
          onClick={() => setShowCreatePeriod((v) => !v)}
        >
          {showCreatePeriod ? "Hide period form" : "New leave period"}
        </button>
      </div>

      {!periods.length ? (
        <div className="alert alert-warning py-2">
          No leave period yet. Create <strong>Leave Quota FY 2026-27</strong>{" "}
          (1 Jul 2026 → 30 Jun 2027) below, then load the staff preview.
        </div>
      ) : null}

      {showCreatePeriod || !periods.length ? (
        <div className="row g-2 mb-3 p-3 bg-white border rounded border-danger">
          <div className="col-12">
            <strong className="text-danger">Step 1 — Create leave period</strong>
          </div>
          <div className="col-md-4">
            <label className="form-label">Period name</label>
            <input
              className="form-control"
              value={newPeriod.name}
              onChange={(e) => setNewPeriod({ ...newPeriod, name: e.target.value })}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Start</label>
            <input
              type="date"
              className="form-control"
              value={newPeriod.start_date}
              onChange={(e) => setNewPeriod({ ...newPeriod, start_date: e.target.value })}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">End</label>
            <input
              type="date"
              className="form-control"
              value={newPeriod.end_date}
              onChange={(e) => setNewPeriod({ ...newPeriod, end_date: e.target.value })}
            />
          </div>
          <div className="col-md-2 d-flex align-items-end">
            <button type="button" className="btn btn-danger w-100" onClick={createPeriod} disabled={loading}>
              {loading ? "Saving…" : "Create & activate"}
            </button>
          </div>
        </div>
      ) : null}

      <div className="row g-2 align-items-end mb-3">
        <div className="col-md-3">
          <label className="form-label">Leave period</label>
          <select
            className="form-select"
            value={periodId}
            onChange={(e) => setPeriodId(e.target.value)}
          >
            <option value="">Select period</option>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.start_date} → {p.end_date})
                {Number(p.is_active) === 1 ? " • Active" : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label">Branch</label>
          <select
            className="form-select"
            value={filterBranch}
            disabled={!!branch_id}
            onChange={(e) => setFilterBranch(e.target.value)}
          >
            {branches.map((b) => (
              <option key={String(b.id)} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
        {LEAVE_TYPES.map((type) => (
          <div className="col-md-1" key={type}>
            <label className="form-label">{type}</label>
            <input
              type="number"
              min={0}
              className="form-control"
              value={defaults[type]}
              onChange={(e) =>
                setDefaults({ ...defaults, [type]: Number(e.target.value || 0) })
              }
            />
          </div>
        ))}
        <div className="col-md-2">
          <div className="form-check mt-4">
            <input
              className="form-check-input"
              type="checkbox"
              id="includeCarry"
              checked={includeCarry}
              onChange={(e) => setIncludeCarry(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="includeCarry">
              Carry forward unused balance
            </label>
          </div>
        </div>
        <div className="col-md-2">
          <button
            type="button"
            className="btn btn-danger w-100"
            disabled={loading}
            onClick={loadPreview}
          >
            {loading ? "Loading…" : "Load staff preview"}
          </button>
        </div>
      </div>

      {periodMeta ? (
        <div className="alert alert-secondary py-2 small">
          Period: <strong>{periodMeta.name}</strong> ({periodMeta.start_date} to{" "}
          {periodMeta.end_date}). Taken days are counted from approved leave applications
          in this window.
          {prevWindow ? (
            <>
              {" "}
              Previous balance window: {prevWindow.start_date} → {prevWindow.end_date}.
            </>
          ) : null}
          {summary ? (
            <>
              {" "}
              Staff {summary.staff_count}, already taken this year {summary.needs_attention},
              already assigned {summary.already_assigned}.
            </>
          ) : null}
        </div>
      ) : null}

      {rows.length ? (
        <>
          <div className="d-flex flex-wrap gap-2 mb-2">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={applyDefaultsToAll}>
              Re-apply defaults to grid
            </button>
            <div className="form-check ms-2 mt-1">
              <input
                className="form-check-input"
                type="checkbox"
                id="attentionOnly"
                checked={attentionOnly}
                onChange={(e) => setAttentionOnly(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="attentionOnly">
                Show only staff who already took leave this year
              </label>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-success ms-auto"
              disabled={saving}
              onClick={assignAll}
            >
              {saving ? "Assigning…" : `Assign quotas (${rows.length} staff)`}
            </button>
          </div>

          <div className="table-responsive">
            <table className="table table-sm table-bordered align-middle bg-white">
              <thead>
                <tr>
                  <th>Staff</th>
                  <th>Status</th>
                  {LEAVE_TYPES.map((type) => (
                    <th key={type} colSpan={includeCarry ? 4 : 3} className="text-center">
                      {type}
                    </th>
                  ))}
                </tr>
                <tr>
                  <th />
                  <th />
                  {LEAVE_TYPES.map((type) => (
                    <React.Fragment key={`${type}-sub`}>
                      <th className="text-center">Taken</th>
                      {includeCarry ? <th className="text-center">Prev bal / Carry</th> : null}
                      <th className="text-center">Quota</th>
                      <th className="text-center">Incl.</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr
                    key={row.user_id}
                    className={row.needs_attention ? "table-warning" : undefined}
                  >
                    <td>
                      <strong>{row.name}</strong>
                      <div className="small text-muted">#{row.user_id}</div>
                    </td>
                    <td className="small">
                      {row.needs_attention ? (
                        <span className="badge bg-warning text-dark">Adjust</span>
                      ) : (
                        <span className="badge bg-success">OK</span>
                      )}
                      {row.already_assigned ? (
                        <div className="text-muted mt-1">Already assigned</div>
                      ) : null}
                    </td>
                    {LEAVE_TYPES.map((type) => {
                      const t = row.types[type];
                      return (
                        <React.Fragment key={`${row.user_id}-${type}`}>
                          <td className="text-center fw-semibold">{t.taken_this_period}</td>
                          {includeCarry ? (
                            <td>
                              <div className="small text-muted mb-1">
                                Bal: {t.previous_balance}
                              </div>
                              <input
                                type="number"
                                min={0}
                                className="form-control form-control-sm"
                                value={t.carried_forward}
                                onChange={(e) =>
                                  updateTypeField(
                                    row.user_id,
                                    type,
                                    "carried_forward",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                          ) : null}
                          <td>
                            <input
                              type="number"
                              min={0}
                              className="form-control form-control-sm"
                              value={t.number_of_leaves}
                              onChange={(e) =>
                                updateTypeField(
                                  row.user_id,
                                  type,
                                  "number_of_leaves",
                                  e.target.value
                                )
                              }
                            />
                          </td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={!!t.selected}
                              onChange={(e) =>
                                updateTypeField(row.user_id, type, "selected", e.target.checked)
                              }
                            />
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <p className="text-muted mb-0 small">
          Select a period, set defaults, optionally enable carry forward, then load the staff
          preview. Yellow rows already used leave this fiscal year — adjust their quota, then
          assign everyone in one click.
        </p>
      )}
    </div>
  );
}
